import { useEffect, useEffectEvent } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { IntentType } from "@lib/infrastructure";
import { getHaNTabForKind, HaNContext, HaNViewStoreContext, QueryParamConstants } from "@lib/logic";
import { useRequiredContext } from "@lib/utils";
import { useIntents } from "@hooks";

import { useHaNTabLinkSearch } from "../../data/useHaNBuilding";
import { useAvailableTabs } from "../../useHaNTabs";

const NODE_PARAM = QueryParamConstants.hereAndNow.explore.resourceId;
const INTENT_PARAM = QueryParamConstants.intent;
const LINK_PARAMS = [NODE_PARAM, INTENT_PARAM, QueryParamConstants.hereAndNow.explore.search];

/**
 * Opens the room, area or facility of an app link, such as a QR code at a space, on its tab and floor. It waits for the
 * building's resources, then drops the link from the URL, so it opens once.
 */
export const HaNLinkedNode = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabLinkSearch = useHaNTabLinkSearch();
  const availableTabs = useAvailableTabs();
  const { tab, building, floor, resources, isResourcesLoading } = useRequiredContext(HaNContext);
  const viewStore = useRequiredContext(HaNViewStoreContext);
  const { setIntentIfNotSet } = useIntents();

  const linkedNodeId = searchParams.get(NODE_PARAM);
  const linkedIntent = searchParams.get(INTENT_PARAM);

  const isReady = building.isSettled && !isResourcesLoading;

  const openLinkedNode = useEffectEvent((nodeId: string) => {
    const resource = resources.find(r => r.id === nodeId);
    if (resource) {
      const resourceTab = getHaNTabForKind(resource.type, availableTabs);

      if (resource.floorId !== floor.floorId) floor.selectFloor(resource.floorId);

      viewStore.select(resourceTab, { kind: resource.type, id: resource.id }, { openDetails: true });
      setIntentIfNotSet(IntentType.parse(linkedIntent));

      // Another tab's link carries only the building, so this also drops the app link
      if (resourceTab !== tab) {
        navigate({ pathname: `/now/${resourceTab}`, search: tabLinkSearch }, { replace: true });
        return;
      }
    }

    setSearchParams(
      params => {
        const nextParams = new URLSearchParams(params);
        LINK_PARAMS.forEach(param => nextParams.delete(param));
        return nextParams;
      },
      { replace: true },
    );
  });

  useEffect(
    function openLinkedNodeWhenReady() {
      if (linkedNodeId && isReady) {
        openLinkedNode(linkedNodeId);
      }
    },
    [linkedNodeId, isReady],
  );

  return null;
};
